import { Button } from "@/components/core/buttons/button";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage, FormPrepend } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { EditCorporationNameSchema, type EditNameSchema, editCorporationNameSchema, editNameSchema } from "@/features/case-evaluation/types/counsel";
import { useEditNameMutation } from "@/features/case-record/api/queries/case-records-query";
import cityService from "@/features/dashboard/api/services/city-service";
import provinceService from "@/features/dashboard/api/services/province-service";
import regionService from "@/features/dashboard/api/services/region-service";
import userService from "@/features/dashboard/api/services/user-service";
import { queryClient } from "@/query-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconHelpCircle } from "@tabler/icons-react";
import clsx from "clsx";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import type { z } from "zod";
import Alert from "../core/alert";
import { Checkbox } from "../core/forms/checkbox";
import Combobox from "../core/forms/combobox";
import Tooltip from "../core/tooltip";

export default function EditNameModal({
	user,
}: {
	user?: any;
}) {
	const { closeModal } = useModalControl();

	type IndividualFormValues = z.infer<typeof editNameSchema>;
	type CorporationFormValues = z.infer<typeof editCorporationNameSchema>;
	type FormValues = IndividualFormValues | CorporationFormValues;

	const isIndividual = user.classification === "Individual";

	const defaultValues = useMemo<FormValues>(() => {
		return isIndividual
			? {
					id: user.id,
					first_name: user.user?.first_name ?? "",
					last_name: user.user?.last_name ?? "",
					middle_name: user.user?.middle_name ?? "",
					suffix: user.user?.suffix ?? "",
					corporation: user?.corporation ?? "",
				}
			: {
					id: user.id,
					first_name: user.user?.first_name ?? "",
					last_name: user.user?.last_name ?? "",
					middle_name: user.user?.middle_name ?? "",
					suffix: user.user?.suffix ?? "",
					corporation: user?.corporation ?? "",
				};
	}, [isIndividual, user]);

	const resolver = useMemo(() => zodResolver(isIndividual ? editNameSchema : editCorporationNameSchema), [isIndividual]);

	const form = useForm<FormValues>({
		resolver,
		defaultValues,
	});

	useEffect(() => {
		form.reset(defaultValues);
	}, [defaultValues, form]);

	const editNameMutation = useEditNameMutation();

	const [emailExists, setEmailExists] = useState(false);

	const advanced = true;

	const onSubmit = async (data: any) => {
		await editNameMutation.mutateAsync({
			data,
		});
		queryClient.invalidateQueries({ queryKey: ["case_records"] });
		closeModal();
	};

	console.log(form.formState.errors);

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4 gap-3">
							<div className="grid grid-cols-12 w-full py-1 gap-x-4 gap-y-3">
								{user.classification === "Individual" ? (
									<>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="first_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>First Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} className="uppercase" onBlur={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="middle_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Middle Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} className="uppercase" onBlur={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="last_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Last Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} className="uppercase" onBlur={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="suffix"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Suffix</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} className="uppercase" onBlur={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</>
								) : (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="corporation"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>Name</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-row justify-between w-full pt-4">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" loading={form.formState.isSubmitting}>
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
